import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Bell, ImageIcon, Check } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';

export default function NotificationBell() {
  const { isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const response = await axios.get(`${API}/notifications?limit=10`);
      setNotifications(response.data.notifications);
      setUnreadCount(response.data.unread_count);
    } catch {
      // Notification fetch failed — silent
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchNotifications();
    // Poll every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/read`);
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch {
      // Mark-read failed — silent
    }
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  if (!isAuthenticated) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => { setIsOpen(!isOpen); }}
        className="relative p-2 text-[#A1A1AA] hover:text-[#F5F5F0] transition-colors"
        data-testid="notification-bell"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#D4AF37] text-[#0A0A0A] text-xs flex items-center justify-center font-medium" data-testid="notification-count">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-[#121212] border border-white/10 shadow-xl z-50" data-testid="notification-dropdown">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <span className="text-sm font-medium text-[#F5F5F0]">Notifications</span>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="text-xs text-[#D4AF37] hover:text-[#E5C04B] transition-colors flex items-center gap-1"
                data-testid="mark-all-read-btn"
              >
                <Check size={12} />
                Mark all read
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <Bell className="mx-auto text-[#71717A] mb-2" size={24} />
                <p className="text-[#71717A] text-sm">No notifications yet</p>
                <p className="text-[#52525B] text-xs mt-1">
                  Favorite artworks to follow artists
                </p>
              </div>
            ) : (
              notifications.map(n => (
                <Link
                  key={n.id}
                  to={n.artwork_id ? `/artwork/${n.artwork_id}` : '#'}
                  className={`block px-4 py-3 border-b border-white/5 hover:bg-white/5 transition-colors ${
                    !n.read ? 'bg-[#D4AF37]/5' : ''
                  }`}
                  onClick={() => setIsOpen(false)}
                  data-testid={`notification-${n.id}`}
                >
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      <ImageIcon size={16} className={!n.read ? 'text-[#D4AF37]' : 'text-[#71717A]'} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm leading-snug ${!n.read ? 'text-[#F5F5F0]' : 'text-[#A1A1AA]'}`}>
                        {n.message}
                      </p>
                      <p className="text-xs text-[#52525B] mt-1">{timeAgo(n.created_at)}</p>
                    </div>
                    {!n.read && (
                      <span className="w-2 h-2 bg-[#D4AF37] rounded-full flex-shrink-0 mt-1.5" />
                    )}
                  </div>
                </Link>
              ))
            )}
          </div>

          {/* Footer */}
          <Link
            to="/favorites"
            className="block px-4 py-3 text-center text-xs text-[#D4AF37] hover:bg-white/5 border-t border-white/10 transition-colors"
            onClick={() => setIsOpen(false)}
          >
            View My Favorites
          </Link>
        </div>
      )}
    </div>
  );
}
